import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchAnalyticsData,
  calculateKPIMetrics,
  generateCampaignPerformanceData,
  generateEarningsTrendsData,
  generateROIComparisonData,
  generatePlatformDistributionData,
  fetchLinkClicksAnalytics,
  fetchCampaignPerformanceTable,
} from '../../services/analyticsService';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import QuickActions from '../../components/ui/QuickActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import KPICard from './components/KPICard';
import PerformanceChart from './components/PerformanceChart';
import EarningsChart from './components/EarningsChart';
import ROIChart from './components/ROIChart';
import PlatformChart from './components/PlatformChart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PLATFORM_COLORS = {
  spotify: '#1DB954',
  apple_music: '#fc3c44',
  youtube_music: '#FF0000',
  tiktok_sound: '#010101',
};

const PLATFORM_LABELS = {
  spotify: 'Spotify',
  apple_music: 'Apple Music',
  youtube_music: 'YouTube Music',
  tiktok_sound: 'TikTok Sound',
};

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('6months');
  const [campaignFilter, setCampaignFilter] = useState('all');

  const [kpiMetrics, setKpiMetrics] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [roiData, setRoiData] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [rawCampaigns, setRawCampaigns] = useState([]);
  const [clicksAnalytics, setClicksAnalytics] = useState(null);
  const [clicksLoading, setClicksLoading] = useState(true);
  const [perfTableData, setPerfTableData] = useState([]);
  const [perfTableLoading, setPerfTableLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadAnalyticsData();
      loadClicksAnalytics();
      loadPerfTable();
    }
  }, [user, timeRange]);

  useEffect(() => {
    if (rawCampaigns?.length > 0) {
      applyFilters();
    }
  }, [campaignFilter, rawCampaigns]);

  const loadClicksAnalytics = async () => {
    setClicksLoading(true);
    const { data } = await fetchLinkClicksAnalytics(user?.id);
    setClicksAnalytics(data);
    setClicksLoading(false);
  };

  const loadPerfTable = async () => {
    setPerfTableLoading(true);
    const { data } = await fetchCampaignPerformanceTable(user?.id);
    setPerfTableData(data || []);
    setPerfTableLoading(false);
  };

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);

      const { campaigns } = await fetchAnalyticsData(user?.id, timeRange);
      setRawCampaigns(campaigns);

      const kpis = calculateKPIMetrics(campaigns);
      const performance = generateCampaignPerformanceData(campaigns);
      const earnings = generateEarningsTrendsData(campaigns, timeRange === '3months' ? 3 : timeRange === '12months' ? 12 : 6);
      const roi = generateROIComparisonData(campaigns);
      const platforms = generatePlatformDistributionData(campaigns);

      setKpiMetrics(kpis);
      setPerformanceData(performance);
      setEarningsData(earnings);
      setRoiData(roi);
      setPlatformData(platforms);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filteredCampaigns = rawCampaigns;

    if (campaignFilter !== 'all') {
      filteredCampaigns = rawCampaigns?.filter(c => c?.status === campaignFilter);
    }

    const kpis = calculateKPIMetrics(filteredCampaigns);
    const performance = generateCampaignPerformanceData(filteredCampaigns);
    const roi = generateROIComparisonData(filteredCampaigns);
    const platforms = generatePlatformDistributionData(filteredCampaigns);

    setKpiMetrics(kpis);
    setPerformanceData(performance);
    setRoiData(roi);
    setPlatformData(platforms);
  };

  const handleDownloadReport = () => {
    if (!kpiMetrics || !performanceData?.length) {
      alert('No data available to export');
      return;
    }

    let csvContent = 'Analytics Report\n\n';
    csvContent += 'Key Performance Indicators\n';
    csvContent += `Total Campaigns,${kpiMetrics?.totalCampaigns}\n`;
    csvContent += `Average ROI,${kpiMetrics?.avgROI}\n`;
    csvContent += `Top Performing Campaign,${kpiMetrics?.topPerforming}\n`;
    csvContent += `Growth Rate,${kpiMetrics?.growthRate}\n`;
    csvContent += `Total Reach,${kpiMetrics?.totalReach}\n`;
    csvContent += `Average Engagement,${kpiMetrics?.avgEngagement}%\n`;
    csvContent += `Total Budget,${kpiMetrics?.totalBudget}\n\n`;

    csvContent += 'Campaign Performance\n';
    csvContent += 'Campaign,Reach (K),Engagement,Conversions\n';
    performanceData?.forEach(item => {
      csvContent += `${item?.name},${item?.reach},${item?.engagement},${item?.conversions}\n`;
    });
    csvContent += '\n';

    csvContent += 'Earnings Trends\n';
    csvContent += 'Month,Earnings,Commission,Net\n';
    earningsData?.forEach(item => {
      csvContent += `${item?.month},${item?.earnings},${item?.commission},${item?.net}\n`;
    });
    csvContent += '\n';

    csvContent += 'ROI by Status\n';
    csvContent += 'Status,ROI (%),Budget,Reach (K)\n';
    roiData?.forEach(item => {
      csvContent += `${item?.name},${item?.roi},${item?.budget},${item?.reach}\n`;
    });

    if (clicksAnalytics) {
      csvContent += '\n\nLink Clicks Analytics\n';
      csvContent += `Total Clicks,${clicksAnalytics?.totalClicks}\n\n`;
      csvContent += 'Clicks by Platform\n';
      csvContent += 'Platform,Clicks\n';
      clicksAnalytics?.byPlatform?.forEach(p => {
        csvContent += `${PLATFORM_LABELS?.[p?.platform] || p?.platform},${p?.count}\n`;
      });
      csvContent += '\nClicks by Campaign\n';
      csvContent += 'Campaign,Clicks\n';
      clicksAnalytics?.byCampaign?.forEach(c => {
        csvContent += `${c?.label},${c?.count}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActions />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Comprehensive performance insights and reporting</p>
          </div>
          <Button
            onClick={handleDownloadReport}
            className="flex items-center gap-2"
          >
            <Icon name="Download" size={18} />
            Download Report
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-2">
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="12months">Last 12 Months</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-2">
                Campaign Status
              </label>
              <select
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Campaigns</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {kpiMetrics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <KPICard
              icon="Briefcase"
              label="Total Campaigns"
              value={kpiMetrics?.totalCampaigns}
              change="+12.5%"
              trend="up"
              color="primary"
            />
            <KPICard
              icon="TrendingUp"
              label="Average ROI"
              value={kpiMetrics?.avgROI}
              change="+8.3%"
              trend="up"
              color="success"
            />
            <KPICard
              icon="Award"
              label="Top Performing"
              value={kpiMetrics?.topPerforming}
              change="N/A"
              trend="neutral"
              color="accent"
            />
            <KPICard
              icon="BarChart3"
              label="Growth Rate"
              value={kpiMetrics?.growthRate}
              change="+5.2%"
              trend="up"
              color="secondary"
            />
          </div>
        )}

        {/* ── REAL LINK CLICKS ANALYTICS SECTION ── */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="MousePointerClick" size={20} color="var(--color-primary)" />
            <h2 className="text-xl font-bold text-foreground">Link Click Analytics</h2>
            <span className="text-xs text-muted-foreground ml-1">(live data from Supabase)</span>
          </div>

          {clicksLoading ? (
            <div className="flex items-center justify-center h-32 bg-card border border-border rounded-2xl">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Total Clicks KPI */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-card border border-border rounded-2xl p-5 lg:col-span-1">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-primary/10 text-primary">
                    <Icon name="MousePointerClick" size={20} />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{clicksAnalytics?.totalClicks?.toLocaleString() || '0'}</p>
                  <p className="text-sm font-medium text-foreground mt-1">Total Clicks</p>
                  <p className="text-xs text-muted-foreground mt-0.5">All tracked link clicks</p>
                </div>
                <div className="bg-card border border-border rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-green-500/10 text-green-500">
                    <Icon name="Layers" size={20} />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{clicksAnalytics?.byPlatform?.length || '0'}</p>
                  <p className="text-sm font-medium text-foreground mt-1">Platforms Tracked</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Unique platforms with clicks</p>
                </div>
                <div className="bg-card border border-border rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-purple-500/10 text-purple-500">
                    <Icon name="Users" size={20} />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{clicksAnalytics?.byInfluencer?.length || '0'}</p>
                  <p className="text-sm font-medium text-foreground mt-1">Influencers Active</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Influencers with tracked clicks</p>
                </div>
                <div className="bg-card border border-border rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-orange-500/10 text-orange-500">
                    <Icon name="Megaphone" size={20} />
                  </div>
                  <p className="text-3xl font-bold text-foreground">{clicksAnalytics?.byCampaign?.length || '0'}</p>
                  <p className="text-sm font-medium text-foreground mt-1">Campaigns Tracked</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Campaigns with click data</p>
                </div>
              </div>

              {/* Clicks by Platform */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h3 className="text-base font-semibold text-foreground mb-4">Clicks by Platform</h3>
                  {clicksAnalytics?.byPlatform?.length === 0 ? (
                    <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No click data yet</div>
                  ) : (
                    <div className="space-y-3">
                      {clicksAnalytics?.byPlatform?.map((item) => {
                        const pct = clicksAnalytics?.totalClicks > 0
                          ? Math.round((item?.count / clicksAnalytics?.totalClicks) * 100)
                          : 0;
                        const color = PLATFORM_COLORS?.[item?.platform] || 'var(--color-primary)';
                        const label = PLATFORM_LABELS?.[item?.platform] || item?.platform;
                        return (
                          <div key={item?.platform} className="flex items-center gap-3">
                            <div className="w-28 text-sm font-medium text-foreground flex-shrink-0 truncate">{label}</div>
                            <div className="flex-1 bg-muted rounded-full h-2.5">
                              <div
                                className="h-2.5 rounded-full transition-all"
                                style={{ width: `${pct}%`, backgroundColor: color }}
                              />
                            </div>
                            <div className="text-sm font-semibold text-foreground w-12 text-right flex-shrink-0">{item?.count}</div>
                            <div className="text-xs text-muted-foreground w-10 text-right flex-shrink-0">{pct}%</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Clicks by Campaign */}
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h3 className="text-base font-semibold text-foreground mb-4">Clicks by Campaign</h3>
                  {clicksAnalytics?.byCampaign?.length === 0 ? (
                    <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No campaign click data yet</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart
                        data={clicksAnalytics?.byCampaign?.slice(0, 8)?.map(c => ({
                          name: c?.label?.length > 16 ? c?.label?.substring(0, 16) + '…' : c?.label,
                          clicks: c?.count,
                        }))}
                        margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
                        <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
                        <Tooltip
                          contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                          labelStyle={{ color: 'var(--foreground)' }}
                          formatter={(value) => [`${value} clicks`, 'Clicks']}
                        />
                        <Bar dataKey="clicks" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Clicks by Influencer */}
              {clicksAnalytics?.byInfluencer?.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-6 mb-6">
                  <h3 className="text-base font-semibold text-foreground mb-4">Clicks by Influencer</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 text-muted-foreground font-medium">#</th>
                          <th className="text-left py-2 text-muted-foreground font-medium">Influencer ID</th>
                          <th className="text-right py-2 text-muted-foreground font-medium">Total Clicks</th>
                          <th className="text-right py-2 text-muted-foreground font-medium">Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clicksAnalytics?.byInfluencer?.slice(0, 10)?.map((inf, idx) => {
                          const pct = clicksAnalytics?.totalClicks > 0
                            ? ((inf?.count / clicksAnalytics?.totalClicks) * 100)?.toFixed(1)
                            : '0.0';
                          return (
                            <tr key={inf?.influencerId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                              <td className="py-3 text-muted-foreground">{idx + 1}</td>
                              <td className="py-3 text-foreground font-mono text-xs">{inf?.influencerId?.substring(0, 16)}…</td>
                              <td className="py-3 text-right font-semibold text-foreground">{inf?.count}</td>
                              <td className="py-3 text-right text-muted-foreground">{pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* ── END LINK CLICKS ANALYTICS ── */}

        {/* ── CAMPAIGN PERFORMANCE TABLE ── */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="TableProperties" size={20} color="var(--color-primary)" />
            <h2 className="text-xl font-bold text-foreground">Campaign Performance</h2>
            <span className="text-xs text-muted-foreground ml-1">(per influencer)</span>
          </div>

          {perfTableLoading ? (
            <div className="flex items-center justify-center h-32 bg-card border border-border rounded-2xl">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : perfTableData?.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="TableProperties" size={24} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-muted-foreground text-sm">No performance data yet. Clicks will appear here once influencers start driving traffic.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-5 py-3 text-muted-foreground font-semibold">#</th>
                      <th className="text-left px-5 py-3 text-muted-foreground font-semibold">Influencer Name</th>
                      <th className="text-right px-5 py-3 text-muted-foreground font-semibold">Total Views</th>
                      <th className="text-right px-5 py-3 text-muted-foreground font-semibold">Total Clicks</th>
                      <th className="text-right px-5 py-3 font-semibold" style={{ color: '#1DB954' }}>Spotify</th>
                      <th className="text-right px-5 py-3 font-semibold" style={{ color: '#fc3c44' }}>Apple Music</th>
                      <th className="text-right px-5 py-3 font-semibold" style={{ color: '#FF0000' }}>YouTube Music</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perfTableData?.map((row, idx) => (
                      <tr
                        key={row?.influencerId}
                        className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-5 py-3 text-muted-foreground">{idx + 1}</td>
                        <td className="px-5 py-3 font-medium text-foreground">{row?.influencerName}</td>
                        <td className="px-5 py-3 text-right text-foreground">{row?.totalViews?.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right font-semibold text-foreground">{row?.totalClicks?.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right" style={{ color: '#1DB954' }}>{row?.spotify?.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right" style={{ color: '#fc3c44' }}>{row?.apple_music?.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right" style={{ color: '#FF0000' }}>{row?.youtube_music?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {/* ── END CAMPAIGN PERFORMANCE TABLE ── */}

        {/* Charts Grid */}
        <div className="space-y-6">
          {performanceData?.length > 0 && (
            <PerformanceChart
              data={performanceData}
              title="Campaign Performance Metrics"
            />
          )}

          {earningsData?.length > 0 && (
            <EarningsChart
              data={earningsData}
              title="Earnings Trends Over Time"
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {roiData?.length > 0 && (
              <ROIChart
                data={roiData}
                title="ROI Comparison by Status"
              />
            )}
            {platformData?.length > 0 && (
              <PlatformChart
                data={platformData}
                title="Platform Distribution"
              />
            )}
          </div>
        </div>

        {(!performanceData || performanceData?.length === 0) && (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="BarChart3" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Analytics Data</h3>
            <p className="text-muted-foreground mb-6">
              Start creating campaigns to see performance analytics and insights.
            </p>
            <Button onClick={() => window.location.href = '/campaign-management'}>
              Create Campaign
            </Button>
          </div>
        )}
      </main>
      <MobileNav />
    </div>
  );
};

export default AnalyticsDashboard;